import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ArrowRight, GoogleLogo } from "@phosphor-icons/react";

const GOOGLE_URL = "https://www.google.com/maps/search/?api=1&query=JS+Blond+Salon+Coworking+Leingarten";

export const ReviewsSection = () => {
  const [data, setData] = useState({ reviews: [], average: null, count: 0 });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API_URL}/api/reviews`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {});
  }, [API_URL]);

  const { reviews, average } = data;
  const ratingLabel = average ? average.toFixed(1).replace(".", ",") : "5,0";

  return (
    <section className="py-16 bg-white" data-testid="reviews-section">
      <div className="container-padding">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="caption block mb-4">Kundenstimmen</span>
          <h2 className="text-3xl md:text-4xl font-normal tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Was unsere Kunden sagen
          </h2>
          <div className="flex items-center justify-center gap-1 mb-2">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} size={24} className="text-[#D4AF37]" weight="fill" />)}
          </div>
          <p className="text-[#2C2C2C]/60 text-sm inline-flex items-center gap-2">
            <GoogleLogo size={16} weight="bold" /> {ratingLabel} Sterne auf Google
          </p>
        </motion.div>

        {reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-10">
            {reviews.slice(0, 6).map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#F9F7F2] p-6 rounded-lg flex flex-col"
                data-testid={`review-card-${review.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} size={14} weight="fill" className={i <= review.rating ? "text-[#D4AF37]" : "text-[#E6E2D6]"} />
                    ))}
                  </div>
                  <GoogleLogo size={16} className="text-[#9A8C73]" weight="bold" />
                </div>
                <p className="text-[#2C2C2C]/80 text-sm leading-relaxed italic mb-4 flex-grow">„{review.text}"</p>
                <p className="text-xs text-[#9A8C73] font-medium">
                  – {review.name}{review.date ? <span className="text-[#2C2C2C]/40 font-normal"> · {review.date}</span> : null}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <motion.a
            href={GOOGLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-[#9A8C73] hover:text-[#2C2C2C] transition-colors uppercase tracking-widest font-medium"
            whileHover={{ scale: 1.02 }}
            data-testid="google-reviews-link"
          >
            Alle Bewertungen auf Google ansehen
            <ArrowRight size={16} />
          </motion.a>
        </div>
      </div>
    </section>
  );
};
